import { configApi } from '@/services/config/config-api';
import type { ConfigDTO } from '@/types/config';
import { useAuthStore } from '@/store/useAuthStore';
import { invalidateConfigKeysCache } from './config-keys-store';

const LS_PREFIX = 'bsc.config.full.';

type ConfigDict = Record<string, ConfigDTO>;

type MemoryCache = {
  tenantId: string | null;
  promise: Promise<ConfigDict> | null;
  value: ConfigDict | null;
};

const memory: MemoryCache = {
  tenantId: null,
  promise: null,
  value: null,
};

function resolveTenantId(explicit?: string | null): string {
  if (explicit) return explicit;
  try {
    return useAuthStore.getState().tenantID || '';
  } catch {
    return '';
  }
}

function lsKey(tenantId: string): string {
  return `${LS_PREFIX}${tenantId || '_'}`;
}

function readFromLocalStorage(tenantId: string): ConfigDict | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(lsKey(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as ConfigDict;
  } catch {
    return null;
  }
}

function writeToLocalStorage(tenantId: string, dict: ConfigDict): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(lsKey(tenantId), JSON.stringify(dict));
  } catch {
    // ignore quota / private mode
  }
}

function removeFromLocalStorage(tenantId?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (tenantId) {
      localStorage.removeItem(lsKey(tenantId));
      return;
    }
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(LS_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

async function fetchConfigFullFromApi(): Promise<ConfigDict> {
  const res = (await configApi.getFull()) as unknown as {
    data?: { data?: unknown } | Record<string, ConfigDTO>;
  };
  const level1 = res?.data;
  if (level1 && typeof level1 === 'object' && 'data' in level1) {
    const nested = (level1 as { data?: unknown }).data;
    return (nested && typeof nested === 'object' ? nested : level1) as ConfigDict;
  }
  return (level1 ?? {}) as ConfigDict;
}

/**
 * Lấy toàn bộ cấu hình tenant (getfull), cache memory + localStorage.
 */
export async function getConfigFull(force = false, tenantId?: string | null): Promise<ConfigDict> {
  const tid = resolveTenantId(tenantId);

  // Cache hit
  if (!force && memory.value && memory.tenantId === tid) {
    return memory.value;
  }

  // DEDUP: nếu đang fetch cùng tenant, reuse promise
  if (!force && memory.promise && memory.tenantId === tid) {
    return memory.promise;
  }

  // Auto-restore: nếu memory trống, thử restore từ localStorage
  if (!force && !memory.value && tid) {
    const fromLs = readFromLocalStorage(tid);
    if (fromLs && Object.keys(fromLs).length) {
      memory.tenantId = tid;
      memory.value = fromLs;
      return fromLs;
    }
  }

  // Memory trống hoàn toàn hoặc force -> gọi API
  memory.tenantId = tid;
  memory.promise = fetchConfigFullFromApi()
    .then((dict) => {
      memory.value = dict;
      writeToLocalStorage(tid, dict);
      return dict;
    })
    .catch((err) => {
      memory.promise = null;
      throw err;
    });

  return memory.promise;
}

export function getConfigFlagFromDict(
  dict: ConfigDict | null | undefined,
  key: string,
  defaultValue = false,
): boolean {
  if (!dict) return defaultValue;
  const entry =
    dict[key] ??
    Object.values(dict).find((c) => (c?.key || '').toLowerCase() === key.toLowerCase());
  if (!entry) return defaultValue;
  // Switch configs often live in `content`; empty content must fall through to `value`
  // (same as cau-hinh getSwitchValue — `??` would keep "").
  const raw = entry.content || entry.value;
  if (raw === undefined || raw === null || raw === '') return defaultValue;
  return String(raw).trim().toLowerCase() === 'true';
}

export async function getConfigFlag(
  key: string,
  defaultValue = false,
  options?: { force?: boolean; tenantId?: string | null },
): Promise<boolean> {
  try {
    const dict = await getConfigFull(options?.force ?? false, options?.tenantId);
    return getConfigFlagFromDict(dict, key, defaultValue);
  } catch {
    return defaultValue;
  }
}

/** Xóa cache memory + localStorage (toàn bộ tenant hoặc 1 tenant). */
export function invalidateConfigFullCache(tenantId?: string | null): void {
  const tid = tenantId ?? memory.tenantId;
  memory.promise = null;
  memory.value = null;
  memory.tenantId = null;
  removeFromLocalStorage(tid);
}

/** Xóa tất cả config cache (full + keys) khi logout. */
export function clearAllConfigCache(): void {
  // Clear keys cache
  invalidateConfigKeysCache();
  // Clear full cache
  invalidateConfigFullCache();
}
